import { WEB_ICO } from '../shell/icons';
import { LoadingState } from '../components/ScreenState';
import type { GamificationView } from '../api/progress.types';

const DAYS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

interface Props {
  narrow?: boolean;
  name?: string | null;
  email?: string | null;
  gamification: GamificationView | null;
  loading?: boolean;
  onOpenSettings: () => void;
  onOpenPlaceholder: (title: string) => void;
  onLogout: () => void;
}

export function WebProfileScreen({ narrow = false, name, email, gamification, loading, onOpenSettings, onOpenPlaceholder, onLogout }: Props) {
  const displayName = name?.trim() || email || 'Bạn';
  const avatarLetter = displayName[0]!.toUpperCase();

  const metrics = [
    { value: `${gamification?.streak ?? 0} ngày`, label: 'Streak', ember: true },
    { value: (gamification?.xp ?? 0).toLocaleString('vi-VN'), label: 'XP', ember: true },
    { value: `${gamification?.stableCards ?? 0}`, label: 'Thẻ ổn định', ember: false },
  ];

  const menu = ['Tra cứu ngữ pháp', 'Thống kê học tập', 'Cài đặt'];

  // Chưa tải xong → 7 ô trống, chưa đánh dấu hôm nay (tránh nháy sai ngày).
  const week = gamification?.weekActivity ?? { days: DAYS.map(() => false), todayIndex: -1 };
  const studiedDays = week.days.filter(Boolean).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, fontFamily: 'var(--font-vi)', maxWidth: 760 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        <span
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'var(--navy-500)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {avatarLetter}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 21, fontWeight: 700, color: 'var(--ink)', wordBreak: 'break-word' }}>{displayName}</div>
          <span
            style={{
              display: 'inline-block',
              marginTop: 6,
              background: 'var(--surface-tint)',
              color: 'var(--text-secondary)',
              fontSize: 12.5,
              fontWeight: 600,
              padding: '4px 12px',
              borderRadius: 999,
            }}
          >
            {`Level ${gamification?.level ?? 1} · ${gamification?.levelLabel ?? 'Mới bắt đầu'}`}
          </span>
        </div>
      </div>

      {loading ? (
        <LoadingState />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: 12 }}>
          {metrics.map((m) => (
            <div
              key={m.label}
              style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-card)', padding: '18px 16px', textAlign: 'center' }}
            >
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}>{m.value}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontWeight: 600, marginTop: 4 }}>{m.label}</div>
            </div>
          ))}
        </div>
      )}

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Điểm danh tuần này</span>
          {gamification && <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)' }}>{`${studiedDays}/7 ngày`}</span>}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {DAYS.map((d, i) => {
            const done = week.days[i];
            const isToday = i === week.todayIndex;
            const future = week.todayIndex >= 0 && i > week.todayIndex;
            return (
              <div key={d} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: 1, minWidth: 0, opacity: future ? 0.5 : 1 }}>
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: done ? 'rgba(242,128,59,0.2)' : 'var(--surface-tint)',
                    border: isToday ? '2px solid var(--cta-default)' : '1px solid var(--border-default)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {done ? WEB_ICO.check('var(--ember-dark)', 14) : null}
                </span>
                <span style={{ fontSize: 11.5, color: isToday ? 'var(--cta-default)' : 'var(--text-secondary)', fontWeight: isToday ? 700 : 600 }}>{d}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        {menu.map((m, i) => (
          <button
            key={m}
            onClick={() => (m === 'Cài đặt' ? onOpenSettings() : onOpenPlaceholder(m))}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              background: 'none',
              border: 'none',
              borderTop: i === 0 ? '1px solid var(--border-default)' : 'none',
              borderBottom: '1px solid var(--border-default)',
              padding: '16px 2px',
              cursor: 'pointer',
              fontFamily: 'var(--font-vi)',
            }}
          >
            <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--ink)' }}>{m}</span>
            {WEB_ICO.chev('var(--text-secondary)', 18)}
          </button>
        ))}
      </div>

      <button
        onClick={onLogout}
        style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: '8px 0', cursor: 'pointer', fontFamily: 'var(--font-vi)', fontSize: 14.5, fontWeight: 700, color: 'var(--error)' }}
      >
        Đăng xuất
      </button>
    </div>
  );
}
